import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";

const PastChatsSidebar = ({
  pastChats = [],
  currentChatId,
  subject = '',
  chatType = 'tutoring',
  isOpen = false,
  onToggle = null
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true); // Start with sidebar collapsed by default

  // Helper function to get user-friendly chat type labels
  const getChatTypeLabel = (type) => {
    const labels = {
      'tutoring': `${subject?.replace('_', ' ')}`,
      'story_telling': 'Story',
      'show_work': 'Show Work',
      'image_creation': 'Image Creation',
      'creative_writing': 'Creative Writing',
      'language_learning': 'Language Learning',
      'map_explorer': 'Map Explorer'
    };
    return labels[type] || subject?.replace('_', ' ') || 'Chat';
  };

  // Check if mobile on mount and window resize
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      // Start collapsed on both mobile and desktop
      setIsCollapsed(true);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleChatClick = (chatId) => {
    // Navigate to chat with subject and touch params
    const url = `/chats/${chatId}?subject=${encodeURIComponent(subject)}&touch=true`;
    window.location.href = url;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    return date.toLocaleDateString();
  };

  const toggleSidebar = () => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    onToggle?.(!newCollapsedState);
  };

  const handleHomeClick = () => {
    window.location.href = '/';
  };

  return (
    <>
      {/* Floating open button shown when collapsed (independent of sidebar container) */}
      {isCollapsed && (
        <button
          onClick={toggleSidebar}
          className="fixed left-2 top-1/2 -translate-y-1/2 z-50 bg-blue-500 hover:bg-blue-600 text-white rounded-full w-10 h-10 flex items-center justify-center shadow-lg"
          aria-label="Open past chats"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}

      <div className={`${isMobile ? 'fixed left-0 top-0 z-40' : 'relative'} h-full bg-gradient-to-b from-blue-50 to-purple-50 border-r-2 border-blue-200 shadow-lg transition-all duration-300 ease-in-out ${isCollapsed ? (isMobile ? '-translate-x-full' : 'w-0 overflow-hidden') : (isMobile ? 'translate-x-0' : 'w-280')}`} 
           style={{ width: isMobile ? (isCollapsed ? '0' : '100vw') : undefined }}>

        {/* Close Button (inside sidebar when open) */}
        {!isCollapsed && (
          <button
            onClick={toggleSidebar}
            className={`${isMobile ? 'fixed top-4 left-4 z-50' : 'absolute top-4 right-4'} bg-blue-500 hover:bg-blue-600 text-white rounded-full w-10 h-10 flex items-center justify-center shadow-lg transition-all duration-300`}
            aria-label="Close past chats"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        {/* Sidebar Content */}
        {!isCollapsed && (
          <div className="h-full flex flex-col">
          {/* Header */}
          <div className="p-4 border-b border-blue-200">
            <button
              onClick={handleHomeClick}
              className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 transition-colors duration-200 mb-3 p-2 rounded-lg hover:bg-blue-100"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              <span className="font-medium">Home</span>
            </button>

            <a
              href={`/chats/new?chat_type=${chatType}&subject=${encodeURIComponent(subject)}`}
              className="w-full mb-3 bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span>New {getChatTypeLabel(chatType)} Chat</span>
            </a>

            <h3 className="text-lg font-bold text-gray-800 capitalize">
              Past {getChatTypeLabel(chatType)} Chats
            </h3>
          </div>

          {/* Chat List */}
          <div className="flex-1 overflow-y-auto">
            {pastChats && pastChats.length > 0 ? (
              <div className="p-2 space-y-2">
                {pastChats.map((chat) => (
                  <button
                    key={chat.id}
                    onClick={() => handleChatClick(chat.id)}
                    className={`w-full text-left p-3 rounded-lg transition-all duration-200 transform hover:scale-105 hover:shadow-md ${
                      chat.id === currentChatId
                        ? 'bg-blue-500 text-white shadow-lg'
                        : 'bg-white hover:bg-blue-50 text-gray-700 shadow-sm border border-blue-100'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate mb-1">
                          {chat.preview}
                        </p>
                        <p className={`text-xs ${
                          chat.id === currentChatId ? 'text-blue-100' : 'text-gray-500'
                        }`}>
                          {formatDate(chat.updated_at || chat.created_at)}
                        </p>
                      </div>
                      {chat.id === currentChatId && (
                        <div className="ml-2 flex-shrink-0">
                          <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="p-4 text-center text-gray-500">
                <div className="mb-4">
                  <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <p className="text-sm font-medium">No past chats yet</p>
                <p className="text-xs mt-1">Start chatting to see your history here!</p>
              </div>
            )}
          </div>

          {/* Fun Footer for Kids */}
          <div className="p-3 border-t border-blue-200 bg-gradient-to-r from-purple-100 to-blue-100">
            <div className="text-center">
              <div className="text-2xl mb-1">🤖</div>
              <p className="text-xs text-gray-600 font-medium">Keep Learning!</p>
            </div>
          </div>
        </div>
        )}
      </div>
    </>
  );
};

PastChatsSidebar.propTypes = {
  pastChats: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    subject: PropTypes.string,
    created_at: PropTypes.string.isRequired,
    updated_at: PropTypes.string,
    preview: PropTypes.string.isRequired
  })),
  currentChatId: PropTypes.number.isRequired,
  subject: PropTypes.string,
  chatType: PropTypes.string,
  isOpen: PropTypes.bool,
  onToggle: PropTypes.func
};


export default PastChatsSidebar;